import React, { useState } from 'react';
import { Users, BookOpen, Calendar, Trophy, CheckCircle, Clock, Target, Brain, Lightbulb, MessageCircle, Award } from 'lucide-react';

const GroupTraining = () => {
  const [activeTab, setActiveTab] = useState(0);

  const groupBatches = [
    {
      title: "IIT-JEE Foundation ",
      grades: "Class 9-10",
      description: "Early start program building strong fundamentals for future JEE preparation with peer learning environment",
      features: ["Concept building focus", "Interactive problem solving", "Peer discussion forums", "Monthly parent meetings"],
      batchSize: "15-20 Students",
      schedule: "Weekend & Evening slots"
    },
    {
      title: "JEE Main & Advanced ",
      grades: "Class 11-12",
      description: "Intensive preparation for both JEE Main and Advanced with comprehensive coverage and competitive environment",
      features: ["Complete syllabus coverage", "Daily practice problems", "Weekly mock tests", "Doubt clearing sessions"],
      batchSize: "20-25 Students",
      schedule: "Daily classes available"
    },
    {
      title: "NEET Aspirants ",
      grades: "Class 11-12, Droppers",
      description: "Focused medical entrance preparation with emphasis on Biology, Physics, and Chemistry with collaborative learning",
      features: ["NCERT mastery", "Previous year papers", "Biology lab sessions", "Study group activities"],
      batchSize: "20-25 Students",
      schedule: "Morning & Evening batches"
    },
    {
      title: "Board Excellence ",
      grades: "Class 10 & 12",
      description: "Targeted preparation for CBSE/ICSE/State boards with focus on scoring maximum marks through group study",
      features: ["Board exam strategies", "Sample paper practice", "Answer writing techniques", "Revision workshops"],
      batchSize: "25-30 Students",
      schedule: "Flexible timings"
    },
    {
      title: "GATE Engineering ",
      grades: "B.Tech 3rd/4th Year",
      description: "Branch-specific GATE preparation with study groups, discussion sessions and collaborative problem solving",
      features: ["Branch-wise batches", "Numerical problem focus", "Previous year analysis", "Group study sessions"],
      batchSize: "15-20 Students",
      schedule: "Weekend intensive"
    },
    {
      title: "Foundation Programs ",
      grades: "Class 6-8",
      description: "Building strong academic foundation with interactive group learning and conceptual understanding",
      features: ["Interactive learning", "Activity-based teaching", "Olympiad preparation", "Peer learning groups"],
      batchSize: "20-25 Students",
      schedule: "After school hours"
    }
  ];

  const benefits = [
    {
      icon: Users,
      title: "Collaborative Learning",
      description: "Learn with peers, share ideas, and grow together. Group dynamics enhance understanding and retention."
    },
    {
      icon: Brain,
      title: "Competitive Environment",
      description: "Healthy competition motivates students to perform better and achieve their full potential."
    },
    {
      icon: MessageCircle,
      title: "Interactive Sessions",
      description: "Engage in discussions, debates, and group activities that make learning more effective and enjoyable."
    },
    {
      icon: Trophy,
      title: "Cost Effective",
      description: "Quality education at affordable prices. Group batches offer excellent value without compromising on quality."
    }
  ];

  const features = [
    {
      title: "Small Batch Sizes",
      description: "We maintain optimal batch sizes (15-30 students) ensuring every student gets attention while benefiting from group dynamics.",
      icon: Users
    },
    {
      title: "Experienced Faculty",
      description: "Learn from IIT alumni and subject experts with years of teaching experience and proven track records.",
      icon: Award
    },
    {
      title: "Structured Curriculum",
      description: "Well-planned curriculum covering entire syllabus with regular assessments and progress tracking.",
      icon: BookOpen
    },
    {
      title: "Regular Assessments",
      description: "Weekly tests, monthly evaluations, and mock exams to track progress and identify improvement areas.",
      icon: Target
    },
    {
      title: "Study Material",
      description: "Comprehensive study material, practice problems, and previous year papers provided to all batch students.",
      icon: Lightbulb
    },
    {
      title: "Flexible Timings",
      description: "Multiple batch timings available - morning, evening, and weekend slots to suit your schedule.",
      icon: Calendar
    }
  ];

  const successStats = [
    { number: "500+", label: "Students Trained", color: "#58A95B" },
    { number: "95%", label: "Success Rate", color: "#58A95B" },
    { number: "50+", label: "Active Batches", color: "#58A95B" },
    { number: "15", label: "Years Experience", color: "#58A95B" }
  ];

  const testimonials = [
    {
      name: "Sneha Reddy",
      exam: "JEE Main 2024 - 99.2 Percentile",
      text: "The group environment at Olewin helped me stay motivated throughout my preparation. Discussing problems with peers and faculty made complex topics easier to understand."
    },
    {
      name: "Karthik Kumar",
      exam: "NEET 2024 - AIR 1250",
      text: "Being part of a competitive batch pushed me to work harder. The regular tests and group study sessions were instrumental in my NEET success."
    },
    {
      name: "Divya Singh",
      exam: "Class 12 CBSE - 96.8%",
      text: "The Board Excellence batch was perfect for my needs. Interactive classes and group discussions helped me understand concepts clearly and score well."
    }
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', backgroundColor: '#fff' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        color: 'white',
        padding: '100px 20px 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 80% 20%, rgba(88, 169, 91, 0.15) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite'
        }} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center' }}>
            <div>
              <h1 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.8s ease-out',
                lineHeight: '1.2'
              }}>
                Group Training
                <span style={{ color: '#58A95B', display: 'block' }}>Learn Together, Succeed Together</span>
              </h1>
              <p style={{
                fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                marginBottom: '2rem',
                lineHeight: '1.8',
                animation: 'fadeInUp 1s ease-out'
              }}>
                Join our structured group batches and experience the power of collaborative learning. 
                Small batch sizes, competitive environment, and experienced faculty from IIT ensure 
                your academic excellence.
              </p>
              <button style={{
                backgroundColor: '#58A95B',
                border: 'none',
                padding: '15px 40px',
                fontSize: '1.1rem',
                fontWeight: '600',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                animation: 'fadeInUp 1.2s ease-out'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}>
               <a className='text-white' href="#batches">Explore Batches</a> 3
              </button>
            </div>
            <div style={{ textAlign: 'center', animation: 'float 3s ease-in-out infinite' }}>
              <Users size={200} color="#58A95B" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </section>

      {/* Success Stats */}
      <section style={{ padding: '60px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '30px',
            textAlign: 'center'
          }}>
            {successStats.map((stat, idx) => (
              <div key={idx} style={{
                padding: '20px',
                animation: `fadeInUp ${0.5 + idx * 0.2}s ease-out`
              }}>
                <h2 style={{
                  fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                  fontWeight: 'bold',
                  color: stat.color,
                  margin: '0 0 10px 0'
                }}>
                  {stat.number}
                </h2>
                <p style={{ fontSize: '1.1rem', color: '#666', margin: 0, fontWeight: '500' }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ padding: '80px 20px', backgroundColor: '#ffffff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem',
            color: '#000000'
          }}>
            Why Choose <span style={{ color: '#58A95B' }}>Group Training?</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}>
            {benefits.map((benefit, idx) => {
              const IconComponent = benefit.icon;
              return (
                <div key={idx} style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '15px',
                  padding: '30px 20px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  animation: `fadeInUp ${0.5 + idx * 0.2}s ease-out`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.backgroundColor = '#58A95B';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.querySelector('svg').style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.color = '#000';
                  e.currentTarget.querySelector('svg').style.color = '#58A95B';
                }}>
                  <IconComponent size={40} color="#58A95B" style={{ marginBottom: '20px', transition: 'all 0.3s ease' }} />
                  <h5 style={{ fontWeight: '600', marginBottom: '1rem' }}>{benefit.title}</h5>
                  <p style={{ lineHeight: '1.6', margin: 0, opacity: 0.9 }}>{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Group Batches */}
      <section id='batches' style={{ padding: '80px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#000000'
          }}>
            Our <span style={{ color: '#58A95B' }}>Group Batches</span>
          </h2>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: '#666', marginBottom: '3rem' }}>
            Choose from our diverse range of group batches designed for different academic goals
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '30px'
          }}>
            {groupBatches.map((batch, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(88, 169, 91, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #000000 0%, #2a2a2a 100%)',
                  padding: '30px 20px',
                  color: 'white',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: '#58A95B',
                    padding: '5px 15px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {batch.grades}
                  </div>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '10px', paddingRight: '80px' }}>{batch.title}</h4>
                </div>
                <div style={{ padding: '25px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <p style={{ color: '#333', marginBottom: '20px', lineHeight: '1.6' }}>
                    {batch.description}
                  </p>
                  <div style={{ marginBottom: '20px', flex: 1 }}>
                    {batch.features.map((feature, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <CheckCircle size={18} color="#58A95B" style={{ marginRight: '10px', marginTop: '2px', flexShrink: 0 }} />
                        <span style={{ color: '#666', fontSize: '0.95rem' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    borderTop: '1px solid #eee',
                    paddingTop: '15px',
                    marginBottom: '15px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Users size={16} style={{ marginRight: '8px', color: '#58A95B' }} />
                      <span><strong>Batch Size:</strong> {batch.batchSize}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Clock size={16} style={{ marginRight: '8px', color: '#58A95B' }} />
                      <span><strong>Schedule:</strong> {batch.schedule}</span>
                    </div>
                  </div>
                  <button style={{
                    width: '100%',
                    backgroundColor: '#58A95B',
                    border: 'none',
                    padding: '12px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#4a8f4d'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#58A95B'}>
                    Join Batch
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '80px 20px', backgroundColor: '#ffffff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem',
            color: '#000000'
          }}>
            What You <span style={{ color: '#58A95B' }}>Get</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {features.map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <div key={idx} style={{
                  padding: '30px',
                  borderRadius: '15px',
                  border: '2px solid #f0f0f0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#58A95B';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#f0f0f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <IconComponent size={40} color="#58A95B" style={{ marginBottom: '15px' }} />
                  <h5 style={{ fontWeight: '600', marginBottom: '10px', color: '#000' }}>{feature.title}</h5>
                  <p style={{ color: '#666', lineHeight: '1.6', margin: 0 }}>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '80px 20px', backgroundColor: '#ffffffff', color: 'dark' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem'
          }}>
            Student <span style={{ color: '#58A95B' }}>Success Stories</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '30px'
          }}>
            {testimonials.map((testimonial, idx) => (
              <div key={idx} style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '15px',
                padding: '30px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#58A95B';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: '#58A95B',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
                <p style={{ color: '#ddd', fontStyle: 'italic', marginBottom: '20px', lineHeight: '1.6' }}>
                  "{testimonial.text}"
                </p>
                <h6 style={{ color: 'white', fontWeight: 'bold', marginBottom: '5px' }}>{testimonial.name}</h6>
                <p style={{ color: '#58A95B', margin: 0, fontSize: '0.9rem' }}>{testimonial.exam}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            Join Our Next Batch
          </h2>
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', marginBottom: '2rem', lineHeight: '1.6' }}>
            Limited seats available! Enroll now and start your journey towards academic excellence with peers who share your ambitions.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{
              backgroundColor: '#000000',
              border: 'none',
              padding: '15px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#1a1a1a';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#000000';
              e.target.style.transform = 'scale(1)';
            }}>
              Enroll Now
            </button>
            <button style={{
              backgroundColor: 'transparent',
              border: '2px solid white',
              padding: '15px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#58A95B';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'white';
            }}>
              View Schedule
            </button>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 0.8;
          }
        }

        * {
          box-sizing: border-box;
        }

        button {
          font-family: inherit;
        }

        @media (max-width: 768px) {
          section {
            padding: 60px 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default GroupTraining;